import type React from 'react'
import { useCallback, useRef, useState } from 'react'
import { DEFAULT_KERNING } from '@/lib/text/constants/text-defaults'
import { KERNING_MAX, KERNING_MIN, KERNING_PRESET_VALUES } from '../constants/typography-ranges'
import {
  exceedsSpacingInputLimit,
  resolveSpacingCommit,
  validateSpacingInput,
} from '../helpers/spacing-input'
import {
  isKerningPresetValue,
  nearestKerningLabel,
  presetValueForLabel,
  resolvePresetChipLabel,
} from '../helpers/spacing-presets'
import { useSliderCommitGuard } from './use-slider-commit-guard'

/** Format a numeric field for display without unnecessary decimals (0 → "0", 10.5 → "10.5"). */
function formatNumericDisplay(n: number): string {
  return String(Number(n.toFixed(2)))
}

const KERNING_INPUT_RULES = {
  min: KERNING_MIN,
  max: KERNING_MAX,
  rangeMessage: `Value: ${KERNING_MIN}–${KERNING_MAX}`,
  strict: true,
}

interface KerningFieldOptions {
  disabled: boolean
  applyKerningAtCaret: ((value: number) => boolean) | undefined
  restoreActiveEditor: () => void
}

/** Kerning: caret-only (a glyph pair at a collapsed caret), applied via `applyKerningAtCaret`. */
export function useKerningField({
  disabled,
  applyKerningAtCaret,
  restoreActiveEditor,
}: KerningFieldOptions) {
  const [kerning, setKerning] = useState([DEFAULT_KERNING])
  const [kerningInputValue, setKerningInputValue] = useState<string>(
    formatNumericDisplay(DEFAULT_KERNING)
  )
  const [kerningError, setKerningError] = useState(false)
  const [kerningErrorMsg, setKerningErrorMsg] = useState<string>('')
  const kerningInputFocused = useRef(false)
  const kerningLastValidInputRef = useRef<string>('')
  // True only when the caret is collapsed and strictly between two characters of one paragraph —
  // the only state in which Kerning has a well-defined value to read or write.
  const [kerningAvailable, setKerningAvailable] = useState(false)
  // Display-only: true when the current (disabled) selection has more than one kerning value.
  // Never enables the control — Kerning still requires a single collapsed caret to be editable.
  const [kerningMixed, setKerningMixed] = useState(false)

  // `kerningAvailable` alone only reflects "is there a valid glyph pair at the caret" — it never
  // incorporated the panel's own top-level `disabled` (locked/inapplicable selection), so a caret
  // could still leave Kerning live while the rest of the panel was meant to be fully disabled.
  const kerningControlsDisabled = disabled || !kerningAvailable

  // Announced by the slider thumb instead of a bare number ("50" → "50 thousandths of an em").
  const sliderValueText = kerningMixed
    ? 'Mixed'
    : kerningAvailable
      ? `${kerning[0]} thousandths of an em`
      : 'Unavailable'

  const chipValue = resolvePresetChipLabel(
    kerningInputValue,
    false,
    isKerningPresetValue,
    nearestKerningLabel
  )

  /** Applies `value` at the caret and, only if that succeeded, shows it as the committed value. */
  const commitKerning = (value: number): boolean => {
    if (!applyKerningAtCaret?.(value)) return false

    setKerning([value])
    setKerningInputValue(formatNumericDisplay(value))
    kerningLastValidInputRef.current = formatNumericDisplay(value)
    setKerningError(false)
    setKerningErrorMsg('')

    return true
  }

  const handleChipChange = (val: string) => {
    if (kerningControlsDisabled) return

    const v = presetValueForLabel(val, KERNING_PRESET_VALUES)

    if (v !== undefined) commitKerning(v)

    restoreActiveEditor()
  }

  const sliderGuard = useSliderCommitGuard(kerningInputFocused, restoreActiveEditor)

  const handleSliderChange = (val: number[]) => {
    if (kerningControlsDisabled) return

    // Same guard Tracking's slider uses on every value change — without it, a
    // keyboard step calls `restoreActiveEditor` (see `onValueCommit`) first,
    // which refocuses the canvas and fires `selectionchange`; the resync effect
    // then re-runs while `kerningInputFocused.current` is still false and clobbers
    // this update with a stale read taken before `applyKerningAtCaret` below ever
    // wrote the new value — the slider "moves once then snaps back".
    kerningInputFocused.current = true

    commitKerning(val[0])
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // No well-defined glyph pair at the caret, or the panel itself is disabled
    // (no selection/locked/inapplicable) — the input shows a greyed placeholder
    // and must not accept edits even if something dispatches a change event
    // directly (opacity/pointer-events is visual only).
    if (kerningControlsDisabled) return

    const raw = e.target.value

    // 3 whole digits and 2 decimals (legacy limit) — rejecting the change keeps the previous text.
    if (exceedsSpacingInputLimit(raw)) return

    setKerningInputValue(raw)

    // Validation only — nothing is applied until Enter/blur (`handleInputBlur`). Applying each
    // valid keystroke made an intermediate value like "-15" (on the way to "-150") the last valid
    // value, so an out-of-range commit then reverted to it instead of the real last value (AC12).
    const result = validateSpacingInput(raw, KERNING_INPUT_RULES)

    if (result.kind === 'partial' || result.kind === 'valid') {
      setKerningError(false)
      setKerningErrorMsg('')
      return
    }

    setKerningError(true)
    setKerningErrorMsg(result.message)
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (kerningControlsDisabled) return
    // Never intercept Undo/Redo (or any other modified shortcut) — only plain/
    // Shift arrows step Kerning here.
    if (e.ctrlKey || e.metaKey || e.altKey) return

    if (e.key === 'Enter') {
      // Per AC12: commit-or-revert on Enter the same way blur does, rather than
      // leaving an invalid typed value sitting in the field indefinitely.
      e.preventDefault()
      ;(e.target as HTMLInputElement).blur()
      return
    }

    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return

    e.preventDefault()

    const current = parseFloat(kerningInputValue)
    const base = isFinite(current) ? current : kerning[0]
    // Mirrors the Kerning Slider's own Radix keyboard convention
    // (step=1, Shift = 10x step) so the input and slider step identically.
    const step = e.shiftKey ? 10 : 1
    const next = e.key === 'ArrowUp' ? base + step : base - step
    const clamped = Math.max(KERNING_MIN, Math.min(KERNING_MAX, next))

    commitKerning(clamped)

    // `applyKerningAtCaret` can move focus back to the shape-text editor (a
    // selection restore) — refocus this input so repeated arrow presses keep
    // incrementing instead of the next press landing on the canvas. Mirrors the
    // Font Size input's own ArrowUp/Down handler.
    ;(e.target as HTMLInputElement).focus()
  }

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    kerningInputFocused.current = true
    e.target.select()
  }

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    kerningInputFocused.current = false

    if (kerningControlsDisabled) return

    // Enter blurs too (see `handleInputKeyDown`), so this is the single commit point.
    const committed = resolveSpacingCommit(e.target.value, KERNING_INPUT_RULES)
    const lastValid = kerningLastValidInputRef.current
    const lastValidN = parseFloat(lastValid)

    const applied = committed !== null && committed !== lastValidN && commitKerning(committed)

    if (!applied) {
      // Blank, '-', out of range, unchanged, or the caret apply failed: restore the last committed
      // value — the canvas was never touched while typing, so it's already correct — and clear
      // the error, since the field is valid again once restored. Also normalizes "10." to "10".
      setKerningInputValue(lastValid)
      setKerning([isFinite(lastValidN) ? lastValidN : DEFAULT_KERNING])
      setKerningError(false)
      setKerningErrorMsg('')
    }

    restoreActiveEditor()
  }

  /** Shows `sliderValue` on the slider and `inputText` in the input as the committed value. */
  const showKerning = useCallback((sliderValue: number, inputText: string) => {
    setKerning([sliderValue])
    setKerningInputValue(inputText)
    kerningLastValidInputRef.current = inputText
    setKerningError(false)
    setKerningErrorMsg('')
  }, [])

  /**
   * Live text editing. Kerning is caret-only: `kerningState` comes from the shape-text engine's
   * own selection state (a native textarea), never from window.getSelection() — that API doesn't
   * reflect textarea selection. It's read unconditionally (not nested under the selection style)
   * so it never depends on whether any other run-style field happens to be set on the run at the
   * caret.
   */
  const syncFromLiveEdit = useCallback(
    (kerningState: { available: boolean; value: number } | null, styleMixedKerning: boolean) => {
      const shapeKerningMixed = !kerningState?.available && styleMixedKerning

      setKerningAvailable(kerningState?.available ?? false)
      setKerningMixed(shapeKerningMixed)

      if (kerningInputFocused.current) return

      if (kerningState?.available) {
        const { value } = kerningState

        showKerning(value, formatNumericDisplay(value))
      } else {
        // No well-defined glyph pair at the caret — show a greyed placeholder, not a stale "0".
        // Park a genuinely mixed range's thumb at the slider's minimum (like Tracking's own
        // mixed state) rather than at Natural (0), which would misrepresent it as a real,
        // uniform value.
        showKerning(shapeKerningMixed ? KERNING_MIN : DEFAULT_KERNING, '')
      }
    },
    [showKerning]
  )

  /**
   * Select Tool has a text/shape object selected but isn't editing it: Kerning is defined at
   * the glyph-pair level, not for a whole text frame, so it's unavailable here. Whether the
   * underlying runs disagree is still surfaced as a display hint (never enables the control).
   */
  const syncFromParagraphs = useCallback(
    (mixed: boolean) => {
      setKerningAvailable(false)
      setKerningMixed(mixed)

      if (!kerningInputFocused.current) {
        // Park a genuinely mixed selection's thumb at the slider's minimum (like the live-editing
        // sync, and Tracking's own mixed state) rather than at Natural (0).
        showKerning(mixed ? KERNING_MIN : DEFAULT_KERNING, '')
      }
    },
    [showKerning]
  )

  /** Nothing selected — no caret exists anywhere, so Kerning has no glyph pair to act on. */
  const resetKerning = useCallback(() => {
    showKerning(DEFAULT_KERNING, '')
    setKerningAvailable(false)
    setKerningMixed(false)
  }, [showKerning])

  return {
    kerning,
    kerningInputValue,
    kerningError,
    kerningErrorMsg,
    kerningAvailable,
    kerningMixed,
    kerningControlsDisabled,
    chipValue,
    sliderValueText,
    sliderGuard,
    handleChipChange,
    handleSliderChange,
    handleInputChange,
    handleInputKeyDown,
    handleInputFocus,
    handleInputBlur,
    syncFromLiveEdit,
    syncFromParagraphs,
    resetKerning,
  }
}

export type KerningField = ReturnType<typeof useKerningField>
